import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import {
  Building2, Users, TrendingDown, Sparkles, ShieldCheck, ArrowRight, Loader2, Trash2, Plus,
  CheckCircle2, Download, Copy, Link2, FileText, Upload,
} from "lucide-react";
import { API } from "@/lib/api";

const PAYPAL_CLIENT_ID = process.env.REACT_APP_PAYPAL_CLIENT_ID;

let scriptPromise = null;
function loadPayPalScript() {
  if (scriptPromise) return scriptPromise;
  scriptPromise = new Promise((resolve) => {
    if (window.paypal) return resolve(true);
    if (!PAYPAL_CLIENT_ID) return resolve(false);
    const s = document.createElement("script");
    s.src = `https://www.paypal.com/sdk/js?client-id=${encodeURIComponent(PAYPAL_CLIENT_ID)}&currency=USD&intent=capture&disable-funding=credit,card`;
    s.async = true;
    s.onload = () => resolve(true);
    s.onerror = () => resolve(false);
    document.body.appendChild(s);
  });
  return scriptPromise;
}

function PricingTable({ tiers, baseUsd, seats }) {
  return (
    <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
      {tiers.map((t, i) => {
        const active = seats >= t.min_seats && (i === tiers.length - 1 || seats < tiers[i + 1].min_seats);
        return (
          <div
            key={t.min_seats}
            className={`p-4 rounded-lg border-2 transition ${active ? "border-red-600 bg-red-50" : "border-zinc-200 bg-white"}`}
            data-testid={`corp-tier-${t.min_seats}`}
          >
            <div className="text-[10px] font-mono uppercase tracking-widest text-zinc-500">{t.label}</div>
            <div className="font-display text-2xl font-black mt-1">${t.price_per_seat}</div>
            <div className="text-[11px] text-zinc-500 font-mono mt-0.5">per seat</div>
            {t.savings_percent > 0 && (
              <div className="mt-2 inline-flex items-center gap-1 text-[10px] font-mono uppercase text-emerald-700">
                <TrendingDown className="h-3 w-3" /> save {t.savings_percent}%
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

function SuccessRoster({ orderRef }) {
  const [data, setData] = useState(null);
  const [origin] = useState(typeof window !== "undefined" ? window.location.origin : "");

  useEffect(() => {
    (async () => {
      try {
        const res = await fetch(`${API}/corporate/order/${orderRef}`);
        if (!res.ok) throw new Error("fetch");
        setData(await res.json());
      } catch (err) {
        console.error("Roster load failed", err);
        toast.error("Could not load order roster");
      }
    })();
  }, [orderRef]);

  const downloadZip = () => {
    window.open(`${API}/corporate/order/${orderRef}/zip`, "_blank");
  };
  const downloadOne = (token) => {
    window.open(`${API}/claim/${token}/pdf`, "_blank");
  };

  if (!data) return <div className="text-sm text-zinc-500 font-mono">Loading roster…</div>;

  const { order, seats } = data;
  return (
    <div className="space-y-6" data-testid="corp-success-roster">
      <div className="bg-emerald-50 border-2 border-emerald-600 rounded-lg p-6">
        <div className="flex items-center gap-3">
          <CheckCircle2 className="h-8 w-8 text-emerald-700" strokeWidth={1.5} />
          <div>
            <div className="text-xs font-mono uppercase tracking-widest text-emerald-800">Payment received</div>
            <div className="font-display text-2xl font-bold text-emerald-900 mt-1">
              {order.seats} certificates issued for {order.company_name}
            </div>
            <div className="text-sm text-emerald-800 mt-1">
              Total paid: ${order.total_usd} · Order {order.order_ref}
            </div>
          </div>
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-3">
        <Button onClick={downloadZip} className="bg-red-600 hover:bg-red-700 text-white" data-testid="corp-download-zip">
          <Download className="mr-2 h-4 w-4" /> Download all as ZIP
        </Button>
        <div className="text-xs text-zinc-500 font-mono">
          or grab any single PDF / claim link below
        </div>
      </div>

      <div className="border border-zinc-200 rounded-lg overflow-hidden bg-white">
        <div className="grid grid-cols-12 bg-zinc-50 border-b border-zinc-200 py-3 px-4 text-[11px] font-mono uppercase tracking-widest text-zinc-500">
          <div className="col-span-4">Recipient</div>
          <div className="col-span-3">Certificate ID</div>
          <div className="col-span-2">Score</div>
          <div className="col-span-3 text-right">Actions</div>
        </div>
        <div className="divide-y divide-zinc-100" data-testid="corp-seats-list">
          {seats.map((s) => {
            const claimUrl = `${origin}/claim/${s.claim_token}`;
            return (
              <div key={s.certificate_id} className="grid grid-cols-12 py-3 px-4 items-center text-sm" data-testid={`corp-seat-${s.certificate_id}`}>
                <div className="col-span-4 min-w-0">
                  <div className="font-medium truncate">{s.user_name}</div>
                  {s.holder_email && <div className="text-xs text-zinc-500 truncate">{s.holder_email}</div>}
                </div>
                <div className="col-span-3 font-mono text-[11px] truncate">{s.certificate_id}</div>
                <div className="col-span-2 font-mono font-bold">{s.score}%</div>
                <div className="col-span-3 flex items-center justify-end gap-1">
                  <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => downloadOne(s.claim_token)} data-testid={`corp-download-${s.certificate_id}`}>
                    <Download className="h-3 w-3 mr-1" /> PDF
                  </Button>
                  <Button
                    size="sm"
                    variant="ghost"
                    className="h-7 text-xs"
                    onClick={() => { navigator.clipboard.writeText(claimUrl); toast.success("Claim link copied"); }}
                    title={claimUrl}
                    data-testid={`corp-copy-${s.certificate_id}`}
                  >
                    <Link2 className="h-3 w-3 mr-1" /> Copy link
                  </Button>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      <div className="text-xs text-zinc-500 border-t border-zinc-200 pt-6">
        <strong className="text-zinc-700">Save this URL:</strong> your team can return to this roster page at any time by bookmarking{" "}
        <code className="font-mono bg-zinc-100 px-1 py-0.5 rounded">{origin}/corporate/order/{orderRef}</code>.
        Each employee can also visit their claim link directly to download their own PDF.
      </div>
    </div>
  );
}

export default function Corporate() {
  const { orderRef: routeOrderRef } = useParams();
  const [tiers, setTiers] = useState([]);
  const [baseUsd, setBaseUsd] = useState(30);
  const [company, setCompany] = useState({ company_name: "", contact_name: "", contact_email: "" });
  const [seats, setSeats] = useState([{ name: "", email: "", score: 90 }]);
  const [quote, setQuote] = useState(null);
  const [order, setOrder] = useState(null); // { paypal_order_id, order_ref, total_usd, seats }
  const [successOrderRef, setSuccessOrderRef] = useState(routeOrderRef || null);
  const [creating, setCreating] = useState(false);
  const [paymentMethod, setPaymentMethod] = useState("paypal"); // "paypal" | "invoice"
  const [invoiceResult, setInvoiceResult] = useState(null);
  const csvFileRef = useRef(null);
  const paypalContainer = useRef(null);

  // --- Draft: auto-save form to localStorage ---
  const DRAFT_KEY = "bbp_corporate_draft_v1";
  const [draftInfo, setDraftInfo] = useState(null); // { savedAt }

  // Restore draft on mount (only when not viewing a completed order)
  useEffect(() => {
    if (routeOrderRef) return;
    try {
      const raw = localStorage.getItem(DRAFT_KEY);
      if (!raw) return;
      const d = JSON.parse(raw);
      if (d.company) setCompany(d.company);
      if (Array.isArray(d.seats) && d.seats.length) setSeats(d.seats);
      if (d.paymentMethod) setPaymentMethod(d.paymentMethod);
      setDraftInfo({ savedAt: d.savedAt });
      toast("Draft restored — pick up where you left off", { icon: "💾" });
    } catch (err) {
      console.error("Draft restore failed", err);
    }
  }, []);

  // Save draft whenever form changes (debounced)
  useEffect(() => {
    if (routeOrderRef || successOrderRef) return;
    const dirty = company.company_name || company.contact_email || seats.some((s) => s.name || s.email);
    if (!dirty) return;
    const id = setTimeout(() => {
      const savedAt = new Date().toISOString();
      try {
        localStorage.setItem(DRAFT_KEY, JSON.stringify({ company, seats, paymentMethod, savedAt }));
        setDraftInfo({ savedAt });
      } catch (err) {
        console.error("Draft persist failed", err);
      }
    }, 600);
    return () => clearTimeout(id);
  }, [company, seats, paymentMethod, routeOrderRef, successOrderRef]);

  const clearDraft = () => {
    try { localStorage.removeItem(DRAFT_KEY); } catch (err) { console.error("Draft clear failed", err); }
    setDraftInfo(null);
    setCompany({ company_name: "", contact_name: "", contact_email: "" });
    setSeats([{ name: "", email: "", score: 90 }]);
    setPaymentMethod("paypal");
    setOrder(null);
    setInvoiceResult(null);
    toast.success("Draft cleared");
  };

  // Load pricing
  useEffect(() => {
    fetch(`${API}/corporate/pricing`)
      .then((r) => r.json())
      .then((d) => { setTiers(d.tiers || []); setBaseUsd(d.base_price_usd || 30); })
      .catch((err) => { console.error("Pricing load failed", err); });
  }, []);

  // Debounced quote whenever seats count changes
  useEffect(() => {
    const n = seats.length;
    if (n < 1) return;
    const id = setTimeout(async () => {
      try {
        const res = await fetch(`${API}/corporate/quote`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ seats: n }),
        });
        setQuote(await res.json());
      } catch (err) { console.error("Quote fetch failed", err); }
    }, 200);
    return () => clearTimeout(id);
  }, [seats.length]);

  const validSeats = useMemo(() => seats.every((s) => s.name.trim().length > 0), [seats]);
  const validContact = company.company_name.trim() && company.contact_name.trim() && company.contact_email.trim();

  const addSeat = () => setSeats([...seats, { name: "", email: "", score: 90 }]);
  const bumpSeats = (n) => {
    const next = [...seats];
    for (let i = 0; i < n; i++) next.push({ name: "", email: "", score: 90 });
    setSeats(next);
  };
  const removeSeat = (i) => setSeats(seats.filter((_, idx) => idx !== i));
  const updateSeat = (i, k, v) => setSeats(seats.map((s, idx) => (idx === i ? { ...s, [k]: v } : s)));

  // --- CSV import ---
  const handleCsvUpload = async (file) => {
    if (!file) return;
    try {
      const text = await file.text();
      const lines = text.split(/\r?\n/).filter((l) => l.trim());
      if (!lines.length) return toast.error("CSV is empty");
      const header = lines[0].split(",").map((h) => h.trim().toLowerCase());
      const idxOf = (key) => header.findIndex((h) => h === key);
      const nameIdx = idxOf("name");
      if (nameIdx === -1) return toast.error("CSV needs a 'name' column");
      const emailIdx = idxOf("email");
      const scoreIdx = idxOf("score");

      const parsed = [];
      for (let i = 1; i < lines.length; i++) {
        const cols = lines[i].split(",").map((c) => c.trim());
        const name = cols[nameIdx] || "";
        if (!name) continue;
        parsed.push({
          name,
          email: emailIdx > -1 ? cols[emailIdx] || "" : "",
          score: scoreIdx > -1 && cols[scoreIdx] ? Math.max(0, Math.min(100, Number(cols[scoreIdx]) || 90)) : 90,
        });
      }
      if (!parsed.length) return toast.error("No valid rows found in CSV");
      setSeats(parsed);
      toast.success(`Imported ${parsed.length} seats from CSV`);
    } catch (err) {
      toast.error(`CSV parse error: ${err.message}`);
    }
  };

  const downloadCsvTemplate = () => {
    const csv = "name,email,score\nJane Doe,jane@example.com,92\nJohn Smith,john@example.com,88\nCharlie Nolan,,90\n";
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "corporate-seats-template.csv";
    a.click();
    URL.revokeObjectURL(url);
  };

  const createOrder = async () => {
    if (!validContact) return toast.error("Fill in your company name, contact name, and email");
    if (!validSeats) return toast.error("Every seat needs a name");
    setCreating(true);
    try {
      const path = paymentMethod === "invoice" ? "/corporate/invoice" : "/corporate/order";
      const res = await fetch(`${API}${path}`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          ...company,
          seats: seats.map((s) => ({ name: s.name.trim(), email: s.email.trim() || null, score: Number(s.score) })),
        }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || "Could not create order");
      }
      const data = await res.json();
      if (paymentMethod === "invoice") {
        setInvoiceResult(data);
        // Clear draft — order is committed
        try { localStorage.removeItem(DRAFT_KEY); } catch (err) { console.error("Draft cleanup failed", err); }
      } else {
        setOrder(data);
      }
    } catch (err) {
      toast.error(err.message);
    } finally {
      setCreating(false);
    }
  };

  // Render PayPal button once an order is created
  useEffect(() => {
    if (!order || !paypalContainer.current) return;
    let cancelled = false;
    (async () => {
      const ok = await loadPayPalScript();
      if (!ok || cancelled) return;
      while (paypalContainer.current.firstChild) {
        paypalContainer.current.removeChild(paypalContainer.current.firstChild);
      }
      const buttons = window.paypal.Buttons({
        style: { layout: "vertical", color: "gold", shape: "pill", label: "paypal", height: 44 },
        createOrder: () => order.paypal_order_id,
        onApprove: async (data) => {
          try {
            const res = await fetch(`${API}/corporate/capture/${data.orderID}`, { method: "POST" });
            if (!res.ok) {
              const err = await res.json().catch(() => ({}));
              throw new Error(err.detail || "Capture failed");
            }
            const captured = await res.json();
            toast.success("Payment complete — your certificates are ready!");
            try { localStorage.removeItem(DRAFT_KEY); } catch (err) { console.error("Draft cleanup failed", err); }
            setSuccessOrderRef(captured.order_ref);
          } catch (err) {
            toast.error(err.message);
          }
        },
        onError: () => toast.error("PayPal error — please try again"),
        onCancel: () => toast("Payment cancelled", { icon: "ℹ️" }),
      });
      if (buttons.isEligible()) buttons.render(paypalContainer.current).catch((err) => console.error("PayPal render failed", err));
    })();
    return () => { cancelled = true; };
  }, [order]);

  if (invoiceResult) {
    const origin = typeof window !== "undefined" ? window.location.origin : "";
    return (
      <div className="min-h-screen bg-[#FAFAFA]">
        <header className="border-b border-zinc-200 bg-white">
          <div className="max-w-3xl mx-auto flex items-center justify-between px-6 py-4">
            <Link to="/" className="flex items-center gap-2">
              <img src="/imcac_logo.png" alt="IMCAC" className="h-9 w-9 object-contain" />
              <span className="font-display font-bold text-sm">IMCAC<span className="text-red-800">.</span></span>
            </Link>
            <div className="text-xs font-mono uppercase tracking-widest text-amber-700">Invoice generated</div>
          </div>
        </header>
        <div className="max-w-3xl mx-auto px-6 py-16">
          <div className="inline-flex items-center gap-2 border border-amber-700 bg-amber-50 px-3 py-1.5 rounded-full mb-4">
            <FileText className="h-3.5 w-3.5 text-amber-700" />
            <span className="text-xs font-mono uppercase tracking-widest text-amber-800">Awaiting wire transfer</span>
          </div>
          <h1 className="font-display text-4xl font-bold tracking-tight" data-testid="corp-invoice-heading">
            Invoice ready — payment due {String(invoiceResult.due_at).slice(0, 10)}.
          </h1>
          <p className="text-zinc-600 mt-3">
            Your invoice has been generated for <strong>${invoiceResult.total_usd}</strong> ({invoiceResult.seats} seats). Download the PDF below and send it to your accounts payable team. Once payment is received, we&rsquo;ll manually activate every certificate and the claim links for your employees.
          </p>

          <div className="mt-8 bg-white border-2 border-amber-600 rounded-lg p-6">
            <div className="text-xs font-mono uppercase tracking-widest text-zinc-500">Invoice number</div>
            <div className="font-mono text-lg font-bold mt-1" data-testid="corp-invoice-ref">{invoiceResult.order_ref}</div>
            <Button
              onClick={() => window.open(`${API}/corporate/invoice/${invoiceResult.order_ref}/pdf`, "_blank")}
              className="mt-4 bg-red-600 hover:bg-red-700 text-white"
              data-testid="corp-invoice-download"
            >
              <Download className="mr-2 h-4 w-4" /> Download PDF invoice
            </Button>
          </div>

          <div className="mt-6 bg-white border border-zinc-200 rounded-lg p-5 text-sm text-zinc-700 space-y-2">
            <div><strong>Next steps:</strong></div>
            <ol className="list-decimal ml-5 space-y-1.5">
              <li>Forward the PDF invoice to your finance team.</li>
              <li>Send payment via wire transfer, ACH, or check within 15 days — reference invoice #<code className="font-mono text-xs bg-zinc-100 px-1 rounded">{invoiceResult.order_ref}</code>.</li>
              <li>Email <a href="mailto:admin@imcac.org" className="text-red-700 underline">admin@imcac.org</a> for our bank wire details or with any questions.</li>
              <li>Once we confirm payment we&rsquo;ll email you the activation confirmation and roster link.</li>
            </ol>
          </div>

          <div className="mt-6 text-xs text-zinc-500 border-t border-zinc-200 pt-4">
            Bookmark this URL to re-download the invoice anytime:{" "}
            <code className="font-mono bg-zinc-100 px-1 rounded">{origin}/corporate/order/{invoiceResult.order_ref}</code>
          </div>
        </div>
      </div>
    );
  }

  if (successOrderRef) {
    return (
      <div className="min-h-screen bg-[#FAFAFA]">
        <header className="border-b border-zinc-200 bg-white">
          <div className="max-w-6xl mx-auto flex items-center justify-between px-6 py-4">
            <Link to="/" className="flex items-center gap-2">
              <img src="/imcac_logo.png" alt="IMCAC" className="h-9 w-9 object-contain" />
              <span className="font-display font-bold text-sm">IMCAC<span className="text-red-800">.</span></span>
            </Link>
            <div className="text-xs font-mono uppercase tracking-widest text-emerald-700">Order complete</div>
          </div>
        </header>
        <div className="max-w-6xl mx-auto px-6 py-12">
          <SuccessRoster orderRef={successOrderRef} />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#FAFAFA]">
      <header className="border-b border-zinc-200 bg-white">
        <div className="max-w-6xl mx-auto flex items-center justify-between px-6 py-4">
          <Link to="/" className="flex items-center gap-2" data-testid="corp-logo">
            <img src="/imcac_logo.png" alt="IMCAC" className="h-9 w-9 object-contain" />
            <span className="font-display font-bold text-sm">IMCAC<span className="text-red-800">.</span></span>
          </Link>
          <div className="text-xs font-mono uppercase tracking-widest text-zinc-500">For teams &amp; employers</div>
        </div>
      </header>

      <div className="max-w-6xl mx-auto px-6 py-12">
        <div className="inline-flex items-center gap-2 border border-red-800 bg-white px-3 py-1.5 rounded-full mb-4">
          <Building2 className="h-3.5 w-3.5 text-red-800" />
          <span className="text-xs font-mono uppercase tracking-widest text-red-800">Corporate bulk purchase</span>
        </div>
        <h1 className="font-display text-4xl md:text-5xl font-bold tracking-tight" data-testid="corp-heading">
          Certify your entire team.<br />
          <span className="text-red-800">One PayPal charge. Volume discount.</span>
        </h1>
        <p className="text-zinc-600 mt-3 max-w-2xl">
          Order 1–500 IMCAC Bloodborne Pathogens certificates in a single payment. We issue each
          certificate immediately and give you a downloadable roster with individual PDFs plus
          shareable claim links for every employee.
        </p>

        {tiers.length > 0 && (
          <div className="mt-8">
            <div className="text-xs font-mono uppercase tracking-widest text-zinc-500 mb-3">Volume pricing (USD)</div>
            <PricingTable tiers={tiers} baseUsd={baseUsd} seats={seats.length} />
          </div>
        )}

        {draftInfo && (
          <div className="mt-6 bg-emerald-50 border border-emerald-200 rounded-lg px-4 py-2.5 flex items-center justify-between text-xs" data-testid="corp-draft-banner">
            <div className="flex items-center gap-2 text-emerald-800">
              <CheckCircle2 className="h-3.5 w-3.5" />
              <span className="font-mono uppercase tracking-widest">Draft auto-saved</span>
              <span className="text-emerald-700">{new Date(draftInfo.savedAt).toLocaleTimeString()}</span>
            </div>
            <button
              type="button"
              onClick={clearDraft}
              className="text-emerald-800 hover:text-emerald-900 underline"
              data-testid="corp-clear-draft"
            >
              Clear draft
            </button>
          </div>
        )}

        <div className="grid lg:grid-cols-12 gap-10 mt-12">
          <div className="lg:col-span-7 space-y-6">
            {/* Company contact */}
            <div className="bg-white border border-zinc-200 rounded-lg p-6">
              <div className="text-xs font-mono uppercase tracking-widest text-red-600 mb-4">Your organization</div>
              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <Label>Company name *</Label>
                  <Input value={company.company_name} onChange={(e) => setCompany({ ...company, company_name: e.target.value })} data-testid="corp-company-name" />
                </div>
                <div />
                <div>
                  <Label>Contact name *</Label>
                  <Input value={company.contact_name} onChange={(e) => setCompany({ ...company, contact_name: e.target.value })} data-testid="corp-contact-name" />
                </div>
                <div>
                  <Label>Contact email *</Label>
                  <Input type="email" value={company.contact_email} onChange={(e) => setCompany({ ...company, contact_email: e.target.value })} data-testid="corp-contact-email" />
                </div>
              </div>
            </div>

            {/* Seats */}
            <div className="bg-white border border-zinc-200 rounded-lg p-6" data-testid="corp-seats-form">
              <div className="flex items-baseline justify-between mb-4 flex-wrap gap-3">
                <div>
                  <div className="text-xs font-mono uppercase tracking-widest text-red-600">Employees</div>
                  <div className="font-display font-bold text-lg mt-0.5">{seats.length} seat{seats.length === 1 ? "" : "s"}</div>
                </div>
                <div className="flex items-center gap-2 flex-wrap">
                  <input
                    ref={csvFileRef}
                    type="file"
                    accept=".csv,text/csv"
                    className="hidden"
                    onChange={(e) => { const f = e.target.files?.[0]; if (f) handleCsvUpload(f); e.target.value = ""; }}
                    data-testid="corp-csv-input"
                  />
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => csvFileRef.current?.click()}
                    data-testid="corp-csv-upload"
                  >
                    <Upload className="h-3.5 w-3.5 mr-1" /> Upload CSV
                  </Button>
                  <button
                    type="button"
                    onClick={downloadCsvTemplate}
                    className="text-[11px] text-zinc-500 hover:text-zinc-800 underline"
                    data-testid="corp-csv-template"
                  >
                    template
                  </button>
                  <div className="w-px h-5 bg-zinc-200 mx-1" />
                  <Button variant="outline" size="sm" onClick={() => bumpSeats(5)} data-testid="corp-add-5">+5</Button>
                  <Button variant="outline" size="sm" onClick={() => bumpSeats(10)} data-testid="corp-add-10">+10</Button>
                  <Button size="sm" onClick={addSeat} className="bg-red-600 hover:bg-red-700 text-white" data-testid="corp-add-seat">
                    <Plus className="h-3.5 w-3.5 mr-1" /> Add seat
                  </Button>
                </div>
              </div>

              <div className="space-y-2 max-h-[400px] overflow-y-auto pr-2">
                {seats.map((s, i) => (
                  <div key={`seat-${i}`} className="grid grid-cols-12 gap-2 items-center" data-testid={`corp-seat-row-${i}`}>
                    <div className="col-span-1 text-xs font-mono text-zinc-400 text-right">{i + 1}.</div>
                    <div className="col-span-5">
                      <Input
                        placeholder="Full name *"
                        value={s.name}
                        onChange={(e) => updateSeat(i, "name", e.target.value)}
                        className="h-9"
                        data-testid={`corp-seat-name-${i}`}
                      />
                    </div>
                    <div className="col-span-4">
                      <Input
                        placeholder="Email (optional)"
                        type="email"
                        value={s.email}
                        onChange={(e) => updateSeat(i, "email", e.target.value)}
                        className="h-9"
                        data-testid={`corp-seat-email-${i}`}
                      />
                    </div>
                    <div className="col-span-1">
                      <Input
                        type="number"
                        min={0}
                        max={100}
                        value={s.score}
                        onChange={(e) => updateSeat(i, "score", e.target.value)}
                        className="h-9 font-mono"
                        data-testid={`corp-seat-score-${i}`}
                      />
                    </div>
                    <div className="col-span-1 flex justify-end">
                      {seats.length > 1 && (
                        <button type="button" onClick={() => removeSeat(i)} className="text-zinc-400 hover:text-red-600 p-1" data-testid={`corp-seat-remove-${i}`}>
                          <Trash2 className="h-4 w-4" />
                        </button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Summary */}
          <div className="lg:col-span-5 space-y-4">
            <div className="bg-zinc-900 text-white rounded-lg p-6 sticky top-6" data-testid="corp-summary">
              <div className="text-xs font-mono uppercase tracking-widest text-red-400 mb-4">Order summary</div>
              <div className="space-y-3 text-sm">
                <div className="flex justify-between">
                  <span className="text-zinc-400">Seats</span>
                  <span className="font-mono font-bold" data-testid="corp-summary-seats">{seats.length}</span>
                </div>
                {quote && (
                  <>
                    <div className="flex justify-between">
                      <span className="text-zinc-400">Price per seat</span>
                      <span className="font-mono">${quote.price_per_seat}</span>
                    </div>
                    {quote.savings_usd > 0 && (
                      <div className="flex justify-between text-emerald-300">
                        <span>Volume savings ({quote.savings_percent}%)</span>
                        <span className="font-mono">− ${quote.savings_usd}</span>
                      </div>
                    )}
                    <div className="border-t border-zinc-700 pt-3 flex justify-between items-baseline">
                      <span className="text-zinc-300">Total</span>
                      <span className="font-display text-3xl font-black" data-testid="corp-summary-total">${quote.total_usd}</span>
                    </div>
                  </>
                )}
              </div>

              {!order ? (
                <>
                  {/* Payment method toggle */}
                  <div className="mt-5 grid grid-cols-2 gap-2 p-1 bg-zinc-800 rounded-md" data-testid="corp-payment-method">
                    <button
                      type="button"
                      onClick={() => setPaymentMethod("paypal")}
                      className={`text-xs font-mono uppercase tracking-widest py-2 rounded-sm transition ${paymentMethod === "paypal" ? "bg-red-600 text-white" : "text-zinc-400 hover:text-white"}`}
                      data-testid="corp-pm-paypal"
                    >
                      Pay by PayPal
                    </button>
                    <button
                      type="button"
                      onClick={() => setPaymentMethod("invoice")}
                      className={`text-xs font-mono uppercase tracking-widest py-2 rounded-sm transition ${paymentMethod === "invoice" ? "bg-red-600 text-white" : "text-zinc-400 hover:text-white"}`}
                      data-testid="corp-pm-invoice"
                    >
                      Invoice / Wire
                    </button>
                  </div>
                  <Button
                    onClick={createOrder}
                    disabled={creating || !quote || !validContact || !validSeats}
                    className="w-full mt-3 bg-red-600 hover:bg-red-700 text-white h-11"
                    data-testid="corp-continue-btn"
                  >
                    {creating ? (
                      <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Preparing…</>
                    ) : paymentMethod === "invoice" ? (
                      <>Generate invoice <FileText className="ml-2 h-4 w-4" /></>
                    ) : (
                      <>Continue to payment <ArrowRight className="ml-2 h-4 w-4" /></>
                    )}
                  </Button>
                  {paymentMethod === "invoice" && (
                    <div className="text-[11px] text-zinc-400 mt-2 leading-relaxed">
                      Certificates are pre-issued but activated only after we receive your wire transfer (Net 15 days).
                    </div>
                  )}
                </>
              ) : (
                <div className="mt-6 space-y-3">
                  <div className="text-xs font-mono uppercase tracking-widest text-emerald-300">Ready to pay ${order.total_usd}</div>
                  <div ref={paypalContainer} data-testid="corp-paypal-container" />
                  <button
                    type="button"
                    className="text-xs text-zinc-400 hover:text-zinc-200 underline"
                    onClick={() => setOrder(null)}
                  >
                    ← Edit seats
                  </button>
                </div>
              )}

              <div className="text-[11px] text-zinc-400 mt-4 flex items-center gap-1.5">
                <ShieldCheck className="h-3 w-3" /> Secured by PayPal · independent competency credential from IMCAC
              </div>
            </div>

            <div className="bg-white border border-zinc-200 rounded-lg p-5 text-xs text-zinc-600 space-y-2">
              <div className="flex items-start gap-2">
                <Sparkles className="h-3.5 w-3.5 text-red-600 flex-shrink-0 mt-0.5" />
                <div>Each seat issues a paid, verifiable competency certificate immediately after payment.</div>
              </div>
              <div className="flex items-start gap-2">
                <Users className="h-3.5 w-3.5 text-red-600 flex-shrink-0 mt-0.5" />
                <div>Download every PDF as a single ZIP, or share individual claim links with employees.</div>
              </div>
              <div className="flex items-start gap-2">
                <Building2 className="h-3.5 w-3.5 text-red-600 flex-shrink-0 mt-0.5" />
                <div>Need to invoice or wire-transfer instead of PayPal? Email <a href="mailto:admin@imcac.org" className="text-red-700 underline">admin@imcac.org</a>.</div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
